import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { Logger } from '../utils/logger';

interface ColorScheme {
  primary: string;
  background: string;
  surface: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textLight: string;
  accent: string;
  success: string;
  warning: string;
  danger: string;
}

interface ColorContextType {
  colors: ColorScheme;
  setColors: (colors: ColorScheme) => void;
  resetColors: () => void;
}

const ColorContext = createContext<ColorContextType | undefined>(undefined);

const defaultColors: ColorScheme = {
  primary: '#5500FF',
  background: '#ffffff',
  surface: '#f8fafc',
  border: '#e2e8f0',
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  textLight: '#ffffff',
  accent: '#333333',
  success: '#10b981',
  warning: '#fbbf24',
  danger: '#ef4444',
};

export function ColorProvider({ children }: { children: ReactNode }) {
  const [colors, setColorsState] = useState<ColorScheme>(defaultColors);

  useEffect(() => {
    // Charger les couleurs depuis localStorage
    const saved = localStorage.getItem('app-color-scheme');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Filter out any old keys that no longer exist in ColorScheme
        const validKeys = Object.keys(defaultColors) as Array<keyof ColorScheme>;
        const cleanedColors: ColorScheme = { ...defaultColors };
        validKeys.forEach((key) => {
          if (parsed[key] !== undefined) {
            cleanedColors[key] = parsed[key];
          }
        });
        setColorsState(cleanedColors);
        // Save the cleaned version back to localStorage
        localStorage.setItem('app-color-scheme', JSON.stringify(cleanedColors));
      } catch (e) {
        Logger.error('Error parsing saved colors:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Appliquer les couleurs aux variables CSS
    document.documentElement.style.setProperty('--color-primary', colors.primary);
    document.documentElement.style.setProperty('--color-background', colors.background);
    document.documentElement.style.setProperty('--color-surface', colors.surface);
    document.documentElement.style.setProperty('--color-surface-hover', colors.surface); // Same as surface for now
    document.documentElement.style.setProperty('--color-surface-elevated', colors.surface); // Same as surface for now
    document.documentElement.style.setProperty('--color-border', colors.border);
    document.documentElement.style.setProperty('--color-text-primary', colors.textPrimary);
    document.documentElement.style.setProperty('--color-text-secondary', colors.textSecondary);
    document.documentElement.style.setProperty('--color-white', colors.background); // White is always the same as background
    document.documentElement.style.setProperty('--color-accent', colors.accent); // Dark accent color for UI elements
    document.documentElement.style.setProperty('--color-button-text', colors.textLight);
    
    // Convert hex to RGB (for glass morphism effects)
    const hexToRgb = (hex: string) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `${r}, ${g}, ${b}`;
    };
    document.documentElement.style.setProperty('--color-background-rgb', hexToRgb(colors.background));
    document.documentElement.style.setProperty('--color-primary-rgb', hexToRgb(colors.primary));
    // V4-DESIGN-RGB-TOKEN — exposer success/danger en RGB pour rgba(var(--color-X-rgb), α).
    document.documentElement.style.setProperty('--color-success-rgb', hexToRgb(colors.success));
    document.documentElement.style.setProperty('--color-danger-rgb', hexToRgb(colors.danger));
    
    // Convert hex to rgba
    const hexToRgba = (hex: string, alpha: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    document.documentElement.style.setProperty('--color-text-muted', hexToRgba(colors.textSecondary, 0.7));
    document.documentElement.style.setProperty('--color-text-light', colors.textLight);
    document.documentElement.style.setProperty('--color-accent-semi', hexToRgba(colors.accent, 0.5));
    
    document.documentElement.style.setProperty('--color-success', colors.success);
    document.documentElement.style.setProperty('--color-warning', colors.warning);
    document.documentElement.style.setProperty('--color-danger', colors.danger);
    document.documentElement.style.setProperty('--color-danger-light', hexToRgba(colors.danger, 0.1));
    // V4-FRONT-COLOR-PRIMARY-LIGHT-UNDEFINED — token consommé par 17 composants (badges,
    // pastilles de filtre, états actifs) mais jamais émise : les fonds tombaient en
    // `transparent`. Même dérivation que `--color-danger-light`.
    document.documentElement.style.setProperty('--color-primary-light', hexToRgba(colors.primary, 0.1));
    
    // Mettre à jour les variables Tailwind pour les UI components (Calendar, etc.)
    // Format: RGB sans virgules (e.g., "255 255 255")
    const hexToTailwindRgb = (hex: string) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `${r} ${g} ${b}`;
    };
    
    document.documentElement.style.setProperty('--background', hexToTailwindRgb(colors.background));
    document.documentElement.style.setProperty('--foreground', hexToTailwindRgb(colors.textPrimary));
    document.documentElement.style.setProperty('--card', hexToTailwindRgb(colors.surface));
    document.documentElement.style.setProperty('--card-foreground', hexToTailwindRgb(colors.textPrimary));
    document.documentElement.style.setProperty('--popover', hexToTailwindRgb(colors.surface));
    document.documentElement.style.setProperty('--popover-foreground', hexToTailwindRgb(colors.textPrimary));
    document.documentElement.style.setProperty('--primary', hexToTailwindRgb(colors.primary));
    document.documentElement.style.setProperty('--primary-foreground', hexToTailwindRgb(colors.textLight));
    document.documentElement.style.setProperty('--muted', hexToTailwindRgb(colors.surface));
    document.documentElement.style.setProperty('--muted-foreground', hexToTailwindRgb(colors.textSecondary));
    document.documentElement.style.setProperty('--accent', hexToTailwindRgb(colors.accent));
    document.documentElement.style.setProperty('--accent-foreground', hexToTailwindRgb(colors.textLight));
    document.documentElement.style.setProperty('--border', hexToTailwindRgb(colors.border));
    document.documentElement.style.setProperty('--input', hexToTailwindRgb(colors.border));
    document.documentElement.style.setProperty('--ring', hexToTailwindRgb(colors.primary));
    document.documentElement.style.setProperty('--switch-background', hexToTailwindRgb(colors.textSecondary));
  }, [colors]);

  const setColors = useCallback((newColors: ColorScheme) => {
    setColorsState(newColors);
    localStorage.setItem('app-color-scheme', JSON.stringify(newColors));
  }, []);

  const resetColors = useCallback(() => {
    setColorsState(defaultColors);
    localStorage.setItem('app-color-scheme', JSON.stringify(defaultColors));
    // Force reload to ensure all components get the updated colors
    window.location.reload();
  }, []);

  const value = useMemo(() => ({ colors, setColors, resetColors }), [colors, setColors, resetColors]);

  return (
    <ColorContext.Provider value={value}>
      {children}
    </ColorContext.Provider>
  );
}

export function useColors() {
  const context = useContext(ColorContext);
  if (context === undefined) {
    throw new Error('useColors must be used within a ColorProvider');
  }
  return context;
}

export { defaultColors };
export type { ColorScheme };